import isApp from "shared/util/isApp";
import OkC from "shared/util/OkcApi";

import getPaypalCallbacks from "shared/payment/helpers/getPaypalCallbacks";

import {
	CREDIT_FORM_NAME_TO_PARAM_MAP,
	CREDIT_CARD_PAYMENT_PROCESSOR,
	PAYPAL_PAYMENT_PROCESSOR,
	PLATFORMS,
} from "shared/payment/constants";

import camelize from "shared/payment/helpers/camelize";
import processProduct from "shared/payment/helpers/processProduct";

export function getPlatform() {
	if (process.env.PLATFORM === "desktop") {
		return PLATFORMS.DESKTOP;
	}
	else if (isApp()) {
		if (window.APP_PLATFORM && window.APP_PLATFORM === "android") {
			return PLATFORMS.ANDROID;
		} else {
			return PLATFORMS.IOS;
		}
	}
	else {
		return PLATFORMS.MOBILE_WEB;
	}
}

const Service = {

	// GET INFO
	// ========

	getUserState() {
		return OkC.get("/billingws/userstate")
			.then((res) => Promise.resolve(camelize(res)));
	},

	getPaypalLink({ productId, ...callbackParams }) {
		const callbacks = getPaypalCallbacks({
			productId,
			...callbackParams,
			platform: getPlatform(),
		});

		return OkC.post("/billingws/paypal/init", {
			data: {
				return_url: `${callbacks}&paypalsuccess=1`,
				cancel_url: `${callbacks}&paypalcancel=1`,
				platform: getPlatform(),
				productid: productId,
			},
		})
			.then((res) => Promise.resolve(camelize(res)));
	},

	getProductPackages({ productType, productSubtype }) {
		return OkC.get("/billingws/ratecard", {
			data: {
				platform: getPlatform(),
				product_type: productType,
				product_subtype: productSubtype,
			},
		})
			.then((res) => Promise.resolve(camelize(res)))
			.then((res) => {
				const { productsList, discount, discountId, group } = res;

				const adaptedProductPackages = productsList
					.sort((res1, res2) => {
						const initialPrice1 = res1.initialPrice;
						const initialPrice2 = res2.initialPrice;

						if (initialPrice1 < initialPrice2) {
							return 1;
						}

						if (initialPrice1 > initialPrice2) {
							return -1;
						}

						return 0;
					})
					.map(processProduct);

				return {
					productPackages: adaptedProductPackages,
					discount: discount || 0,
					discountId: discountId || "",
					rateCardName: group,
				};
			});
	},

	// SEMI-PURCHASE CALLS
	// ===================

	updateCard({ cardholderName, cardNumber, cvv, expiration, zipCode }) {
		// const addNewInfo = !(paymentToken && paymentToken.tokenid);

		return OkC.post("/billingws/creditcard/update", {
			data: {
				platform: getPlatform(),
				tokenid: 0, // use 0 until the endpoint can be updated. 2/26
				[CREDIT_FORM_NAME_TO_PARAM_MAP.cardholderName]: cardholderName,
				[CREDIT_FORM_NAME_TO_PARAM_MAP.cvv]: cvv,
				[CREDIT_FORM_NAME_TO_PARAM_MAP.zipCode]: zipCode,

				// format out display chars from the card and expiry
				[CREDIT_FORM_NAME_TO_PARAM_MAP.cardNumber]: cardNumber.replace(/\D+/g, ""),
				[CREDIT_FORM_NAME_TO_PARAM_MAP.expiration]: expiration.replace(/\D+/g, ""),
			},
		})
			.then((res) => Promise.resolve(camelize(res)));
	},

	savePaypalInfo({ token }) {
		return OkC.post("/billingws/paypal/create", {
			data: {
				platform: getPlatform(),
				paypal_transactionid: token,
			},
		})
			.then((res) => Promise.resolve(camelize(res)));
	},

	// PURCHASE CALLS
	// ==============

	purchaseWithNewCard({
		productId,
		cardholderName,
		cardNumber,
		cvv,
		expiration,
		zipCode,
		discountId,
	}) {
		return OkC.post("/billingws/creditcard/purchase", {
			data: {
				platform: getPlatform(),
				productid: productId,
				discount_type: discountId,
				[CREDIT_FORM_NAME_TO_PARAM_MAP.cardholderName]: cardholderName,
				[CREDIT_FORM_NAME_TO_PARAM_MAP.cvv]: cvv,
				[CREDIT_FORM_NAME_TO_PARAM_MAP.zipCode]: zipCode,

				// format out display chars from the card and expiry
				[CREDIT_FORM_NAME_TO_PARAM_MAP.cardNumber]: cardNumber.replace(/\D+/g, ""),
				[CREDIT_FORM_NAME_TO_PARAM_MAP.expiration]: expiration.replace(/\D+/g, ""),
			},
		})
			.then((res) => Promise.resolve(camelize(res)));
	},

	purchaseWithInfo({ productId, tokenid, paymentProcessor, discountId }) {
		const typeMap = {
			[CREDIT_CARD_PAYMENT_PROCESSOR]: "creditcard",
			[PAYPAL_PAYMENT_PROCESSOR]: "paypal",
		};

		const type = typeMap[paymentProcessor];

		if (!type) {
			return Promise.reject();
		}

		return OkC.post(`/billingws/${type}/purchase`, {
			data: {
				platform: getPlatform(),
				productid: productId,
				discount_type: discountId,
				tokenid,
			},
		})
			.then((res) => Promise.resolve(camelize(res)));
	},

	purchaseWithPaypal({ productid, token, platform, discountid }) {
		return OkC.post("/billingws/paypal/purchase", {
			data: {
				platform,
				productid,
				discount_type: discountid,
				paypal_transactionid: token,
			},
		})
			.then((res) => Promise.resolve(camelize(res)));
	},
};

export default Service;



// WEBPACK FOOTER //
// ./src/shared/payment/services/index.js